import {
  getDaysTillGoalDate,
  getCompactLocalizedDateLabel,
  getBmi,
  getGolaPaceKgValues,
  getGolaPaceLbsValues,
  getFasterProgressDaysDiff,
  getFirstWeekLoss,
  getPredictedPace,
} from "../../../common/js/helpers"

export default (commonView) => () => ({
  ...commonView(),

  init() {
    this.commonInit()

    const data = { segmentUserId: this.store.values.segmentUserId || "" }

    // Init for Klaviyo only

    this.store.internalTracker.isUserIdentified(data, "klaviyo").then((isIdentified) => {
      if (!isIdentified) {
        this.store.internalTracker.trackIdentify({
          customData: {
            email: this.store.values.email,
            segmentUserId: this.store.values.segmentUserId,
          },
          destinationService: "klaviyo",
          callBackFn: () => {
            this.store.internalTracker.trackPageView({}, "klaviyo")
          },
        })
      }
    })
  },

  getGoalWeight() {
    return this.store.isMetric()
      ? this.store.values.targetWeightKg
      : this.store.values.targetWeightLbs
  },

  // Product requirement for the formula:
  // Math.min(5kg, (currentWeight - targetWeight)/3) + (3 * maxGoalPace)
  calculateWeightLoss(currentWeight, targetWeight, maxGoalPace, firstWeekWeightLoss) {
    const weightDiff = Math.abs(currentWeight - targetWeight)
    const initialLoss = Math.min(firstWeekWeightLoss, weightDiff / 3)
    const firstMonthWeightLoss = Math.round(initialLoss + 3 * maxGoalPace)
    const isExcessWeightRemaining = weightDiff >= firstMonthWeightLoss

    return isExcessWeightRemaining ? firstMonthWeightLoss : weightDiff
  },

  getFirstMonthWeightLoss() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    const maxGoalPace = this.store.isMetric()
      ? getGolaPaceKgValues().reverse()[0]
      : getGolaPaceLbsValues().reverse()[0]

    return this.store.isMetric()
      ? this.calculateWeightLoss(weightKg, targetWeightKg, maxGoalPace, 5)
      : this.calculateWeightLoss(weightLbs, targetWeightLbs, maxGoalPace, 11)
  },

  getCurrentMonthLabel() {
    const today = new Date()
    return getCompactLocalizedDateLabel(today)
  },

  getGoalMonthLabel() {
    const isMetric = this.store.isMetric()
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } =
      this.store.values

    const daysTillGoalDate = getDaysTillGoalDate(
      {
        weightKg,
        weightLbs,
        targetWeightKg,
        targetWeightLbs,
        goalPaceKg,
        goalPaceLbs,
      },
      isMetric,
    )

    const minusDays = getFasterProgressDaysDiff(this.store.values, isMetric)
    const goalDate = new Date()
    const daysCount = daysTillGoalDate - minusDays > 0

    if (daysCount) {
      goalDate.setDate(goalDate.getDate() + daysTillGoalDate - minusDays)
    } else {
      goalDate.setDate(goalDate.getDate() + daysTillGoalDate)
    }

    return getCompactLocalizedDateLabel(goalDate)
  },

  getBmi() {
    const isMetric = this.store.isMetric()
    return getBmi(this.store.values, isMetric).toFixed(0)
  },

  getPredictedPace() {
    const isMetric = this.store.isMetric()
    return getPredictedPace(this.store.values, isMetric).toFixed(1)
  },

  getTargetBmi() {
    const { heightCm, heightFt, heightIn, targetWeightKg, targetWeightLbs } = this.store.values

    const values = {
      heightCm,
      heightFt,
      heightIn,
      weightKg: targetWeightKg,
      weightLbs: targetWeightLbs,
    }

    const isMetric = this.store.isMetric()
    return getBmi(values, isMetric).toFixed()
  },

  // BMR formula
  // Men: BMR = 88.362 + (13.397 × weight kg) + (4.799 × height cm) – (5.677 × age)
  // Women: BMR = 447.593 + (9.247 × weight kg) + (3.098 × height cm) – (4.330 × age)
  getBMR() {
    const { weightKg, weightLbs, heightCm, heightFt, heightIn, age, gender } = this.store.values
    const isMetric = this.store.isMetric()

    // Convert to metric if needed
    const weightInKg = isMetric ? weightKg : weightLbs * 0.453592
    const heightInCm = isMetric ? heightCm : (heightFt * 30.48) + (heightIn * 2.54)

    let bmr
    if (gender === 'male') {
      bmr = 88.362 + (13.397 * weightInKg) + (4.799 * heightInCm) - (5.677 * age)
    } else {
      bmr = 447.593 + (9.247 * weightInKg) + (3.098 * heightInCm) - (4.330 * age)
    }

    return Math.round(bmr)
  },

  // Returns calorie range UI string like "1500 - 1700"
  getCalorieRange() {
    const bmr = this.getBMR()
    const lower = Math.round(bmr / 100) * 100
    const upper = lower + 300
    return `${lower} - ${upper}`
  },

  getBmiWidgetPointPosition() {
    const bmi = this.getBmi()

    const scaleMin = 15
    const scaleMax = 35

    if (bmi <= scaleMin) return 0
    if (bmi >= scaleMax) return 100

    return ((bmi - scaleMin) * 100) / (scaleMax - scaleMin)
  },

  getFirstWeekLoss() {
    return getFirstWeekLoss({
      values: this.store.values,
      isMetric: this.store.isMetric(),
    })
  },

  isWeightGainOverThreshold() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values
    const currentWeight = this.store.isMetric() ? weightKg : weightLbs
    const targetWeight = this.store.isMetric() ? targetWeightKg : targetWeightLbs
    const weightDiff = currentWeight - targetWeight

    return this.store.isMetric() ? weightDiff >= 5 : weightDiff >= 10
  },

  getAgeDecade() {
    const age = this.store.values.age?.toString()

    return `${age[0]}0`
  },

  goNextAndTrack(clickName = "Continue Clicked") {
    this.store.internalTracker.setEventToSessionStorage({ clickName })
    this.goNext()
  },
})
